import React, { FC, useState, useEffect, useCallback, useMemo, useRef } from "react";
import { User } from "../../models/users/userInterfaces";
import { Note } from "../../models/applications/applicationInterfaces";
import { useDispatch, useSelector } from "react-redux";
import truncate from "truncate";
import {
  addNote,
  deleteNote,
  selectApplicationDetail,
  updateNote,
} from "../../models/applications/applicationDetailSlice";
import { PencilFill, TrashFill } from "react-bootstrap-icons";
import "./notes.scss";
import { formatDate } from "../../utils/helpers";

interface Props {
  applicant: User;
  notes: Note[];
  activeNote: number;
  setActiveNote: (noteIndex: number) => void;
}

export const Notes: FC<Props> = ({ notes, activeNote, setActiveNote }) => {
  const application = useSelector(selectApplicationDetail);
  const appDetails = useMemo(() => application.application, [application]);
  const permissions = useMemo(
    () => application.application?.permissions,
    [application]
  );
  const canEditNotes = useMemo(
    () => !!permissions?.includes("create-application-note"),
    [permissions]
  );

  // If the following text fields are provided, set the text to "" so that the placeholder text displays instead.
  // This check only runs when the note is loaded... not while the user is typing.
  const formatText = (textField: string, text: string) => {
    if (!text) return "";
    if (textField === "title" && text === "Untitled Note") return "";
    if (textField === "body" && text === "Add your note content here.")
      return "";
    return text;
  };
  const dispatch = useDispatch();

  const BODY_MAX_LENGTH = 2000;
  const [title, setTitle] = useState(() =>
    formatText("title", notes[activeNote]?.title || "")
  );
  const [body, setBody] = useState(() =>
    formatText("body", notes[activeNote]?.body || "")
  );
  const [isLoadingNoteOperation, setIsLoadingNoteOperation] = useState(false);
  const [notesList, setNotesList] = useState(notes);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Track optimistic updates for preview pane
  const optimisticUpdatesRef = useRef<{ [noteId: number]: { title?: string; body?: string } }>({});
  const [, forceUpdate] = useState(0);

  // Queue-based save system
  const saveQueueRef = useRef<Array<{ noteId: number; applicationId: number; title?: string; body?: string }>>([]);
  const isProcessingRef = useRef(false);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isCreatingOrDeletingRef = useRef(false);

  // Check if saves are pending
  const hasPendingSaves = useCallback(() =>
    saveTimeoutRef.current !== null || saveQueueRef.current.length > 0 || isProcessingRef.current,
    []
  );

  // Process the save queue sequentially
  const processSaveQueue = useCallback(async () => {
    if (isProcessingRef.current || saveQueueRef.current.length === 0) return;

    isProcessingRef.current = true;
    while (saveQueueRef.current.length > 0) {
      const saveRequest = saveQueueRef.current.shift();
      if (saveRequest) {
        const result: any = await dispatch(updateNote(saveRequest));
        // TODO: After upgrading @reduxjs/toolkit to >= 1.5.0, use try/catch + .unwrap() for cleaner error handling
        if (result.error) {
          console.error("Failed to save note:", result.error);
          setSaveError("Failed to save note. Please try again.");
          saveQueueRef.current.unshift(saveRequest);
          break;
        } else {
          setSaveError(null); // Clear any previous errors on success
        }
      }
    }
    isProcessingRef.current = false;
  }, [dispatch]);

  // Queue a save request (debounced with coalescing)
  const queueSave = useCallback((noteId: number, applicationId: number, title?: string, body?: string) => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);

    saveTimeoutRef.current = setTimeout(() => {
      saveTimeoutRef.current = null;
      const existingIndex = saveQueueRef.current.findIndex(
        (item) => item.noteId === noteId && item.applicationId === applicationId
      );

      if (existingIndex !== -1) {
        const existing = saveQueueRef.current[existingIndex];
        saveQueueRef.current[existingIndex] = {
          noteId,
          applicationId,
          title: title !== undefined ? title : existing.title,
          body: body !== undefined ? body : existing.body,
        };
      } else {
        saveQueueRef.current.push({ noteId, applicationId, title, body });
      }

      if (!isProcessingRef.current) processSaveQueue();
    }, 250);
  }, [processSaveQueue]);
  // Complete all pending saves before switching notes
  const completePendingSaves = useCallback(async () => {
    if (!hasPendingSaves()) return;

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }

    await processSaveQueue();
    while (isProcessingRef.current) {
      await new Promise(resolve => setTimeout(resolve, 50));
    }
  }, [processSaveQueue, hasPendingSaves]);

  // On initial load, load the first note
  useEffect(() => {
    loadNote(activeNote);
  }, []);

  // When new notes are loaded from Redux's state, sync and clear loading
  useEffect(() => {
    setNotesList(notes);

    // Only clear loading if not in middle of create/delete operation
    if (!isCreatingOrDeletingRef.current) {
      setIsLoadingNoteOperation(false);
    }

    // Clear optimistic updates for notes that have been saved
    notes.forEach(note => {
      const noteId = note.id;
      if (noteId !== undefined && optimisticUpdatesRef.current[noteId]) {
        const optimistic = optimisticUpdatesRef.current[noteId];
        if (
          (optimistic.title === undefined || optimistic.title === note.title) &&
          (optimistic.body === undefined || optimistic.body === note.body)
        ) {
          delete optimisticUpdatesRef.current[noteId];
        }
      }
    });
    forceUpdate(prev => prev + 1);
  }, [notes]);

  // When the active note changes, complete pending saves then update the viewed note
  useEffect(() => {
    const switchNote = async () => {
      const showLoading = hasPendingSaves();
      if (showLoading) setIsLoadingNoteOperation(true);

      await completePendingSaves();

      // Wait a tick to ensure notesList state is synced before loading
      await new Promise(resolve => setTimeout(resolve, 0));
      loadNote(activeNote);

      if (showLoading) {
        // Small delay to ensure DOM updates before clearing loading overlay (prevents flicker)
        await new Promise(resolve => setTimeout(resolve, 50));
        setIsLoadingNoteOperation(false);
      }
    };
    switchNote();
  }, [activeNote, completePendingSaves, hasPendingSaves]);

  // Cleanup: flush queue on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      if (saveQueueRef.current.length > 0 && !isProcessingRef.current) processSaveQueue();
    };
  }, [processSaveQueue]);

  const loadNote = (selectedKey: number, clonedList?: Note[]) => {
    const note = clonedList ? clonedList[selectedKey] : notesList[selectedKey];
    note?.title && setTitle(formatText("title", note.title || ""));
    note?.body && setBody(formatText("body", note.body || ""));
  };

  // Handle changes to note title or body fields
  const handleFieldChange = useCallback((field: 'title' | 'body', value: string) => {
    const setter = field === 'title' ? setTitle : setBody;
    setter(value);

    const currentNote = notes[activeNote];
    const noteId = currentNote?.id;
    if (appDetails?.id && noteId !== undefined) {
      const fieldValue = field === 'title' ? (value || "Untitled Note") : value;

      // Optimistic update for preview
      optimisticUpdatesRef.current[noteId] = {
        ...optimisticUpdatesRef.current[noteId],
        [field]: fieldValue,
      };
      forceUpdate(prev => prev + 1);

      queueSave(
        noteId,
        appDetails.id,
        field === 'title' ? fieldValue : undefined,
        field === 'body' ? value : undefined
      );
    }
  }, [notes, activeNote, appDetails, queueSave, forceUpdate]);

  const newNote = async () => {
    isCreatingOrDeletingRef.current = true;
    setIsLoadingNoteOperation(true);
    await completePendingSaves();
    setActiveNote(0);
    setTitle("");
    setBody("");
    if (appDetails) {
      await dispatch(addNote(appDetails.id));
    }
    isCreatingOrDeletingRef.current = false;
    setIsLoadingNoteOperation(false);
  };

  if (!permissions?.includes("view-application-note")) {
    return <>You do not have permission to view this page.</>;
  }

  return (
    <div className="notes_container">
      {/* Error notification for failed saves */}
      {saveError && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert" style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          zIndex: 1050,
          minWidth: '300px'
        }}>
          {saveError}
          <button type="button" className="close" onClick={() => setSaveError(null)}>
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
      )}
      {/* LEFT COLUMN - NOTES LIST */}
      <div className="notes_list" style={{ position: 'relative' }}>
        {isLoadingNoteOperation && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10,
            pointerEvents: 'none'
          }}>
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Loading...</span>
            </div>
          </div>
        )}
        <div className="container p-0">
          {/* UI MENU - "Add Notes" and Pagination */}
          {canEditNotes && (
            <button className="w-100 btn btn-primary" onClick={newNote}>
              <PencilFill className="mr-2" />
              New Note
            </button>
          )}
        </div>
        {/* Map an individual note list item for each item in data... */}
        {notesList.map((note, key) => {
          // Use optimistic updates if available, otherwise use note data
          const optimisticUpdate = note.id ? optimisticUpdatesRef.current[note.id] : undefined;
          const displayTitle = optimisticUpdate?.title ?? note.title;
          const displayBody = optimisticUpdate?.body ?? note.body;

          return (
            <div
              key={note.id || key}
              className={`${
                key === activeNote && "notes_list__item-active"
              } notes_list__item`}
              onClick={() => setActiveNote(key)}
            >
              <div className="notes_list__item_title">
                {/* Note Title Preview */}
                {displayTitle && truncate(displayTitle, 22)}
                {/* Delete the Note (Trash Icon) */}
                {canEditNotes && (
                  <TrashFill
                    className="trash-hover"
                    onClick={async (e) => {
                      e.stopPropagation();
                      isCreatingOrDeletingRef.current = true;
                      await completePendingSaves();

                      if (note.id) delete optimisticUpdatesRef.current[note.id];

                      const clonedNotesList = Array.from(notesList);
                      clonedNotesList.splice(key, 1);

                      setIsLoadingNoteOperation(true);
                      setNotesList(clonedNotesList);
                      forceUpdate(prev => prev + 1);

                      if (clonedNotesList.length > 0) {
                        setActiveNote(0);
                        loadNote(0, clonedNotesList);
                      }

                      await dispatch(deleteNote({ applicationId: appDetails?.id, noteId: note.id }));
                      isCreatingOrDeletingRef.current = false;
                      setIsLoadingNoteOperation(false);
                    }}
                  />
                )}
              </div>
              {/* Note Body Preview */}
              <div className="notes_list__item_body">
                {displayBody && truncate(displayBody, 58)}
              </div>
            </div>
          );
        })}
      </div>
      {/* RIGHT COLUMN - NOTE VIEWER */}
      <div className="notes_viewer">
        {isLoadingNoteOperation ? (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            width: '100%'
          }}>
            {/* Empty loading state - no spinner, no content */}
          </div>
        ) : notesList.length > 0 ? (
          <>
            <input
              className="notes_viewer__title h1"
              value={title}
              placeholder="Untitled Note"
              onChange={(e) => handleFieldChange('title', e.target.value)}
            />
            <div className="notes_viewer__createdAt">
              Created:{" "}
              {notes[activeNote]?.createdAt
                ? formatDate(notes[activeNote].createdAt || "")
                : "Loading..."}
            </div>
            <div className="notes_viewer__updatedAt">
              Last Updated:{" "}
              {notes[activeNote]?.updatedAt
                ? formatDate(notes[activeNote].updatedAt || "")
                : "Loading..."}
            </div>
            {/* Note Content */}
            <div className="d-flex flex-column h-100">
              <textarea
                className="notes_viewer__body"
                value={body}
                placeholder="Add your note content here."
                onChange={(e) => handleFieldChange('body', e.target.value)}
              />
              <div
                className={`${
                  body.length > BODY_MAX_LENGTH && "text-danger"
                } mt-4 w-100`}
              >
                {body.length} / 2000 characters
              </div>
            </div>
          </>
        ) : (
          <div className="notes_empty">
            {canEditNotes ? (
              <div>
                Click <PencilFill /> "New Note" to add a note
              </div>
            ) : (
              `No notes yet. When your team members add notes, they will appear here.`
            )}
          </div>
        )}
      </div>
    </div>
  );
};
